import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ShareLinkDialog } from '../../shared/components/share-link-dialog/share-link-dialog';
import { PlayerAuctionService } from '../player-auction.service';
import {
  OutsourcingActionRequired,
  OutsourcingHandoffResponse,
  OutsourcingHandoffStatus,
  OUTSOURCING_HANDOFF_TERMINAL_STATUSES,
  SellContractRow,
} from '../player-auction.type';
import { parseBackupCodes, pastedBackupCodes } from '../../shared/credentials';

export interface OutsourcingHandoffDialogData {
  contract: SellContractRow;
}

const POLL_INTERVAL_MS = 5_000;

const HANDOFF_STATUS_LABELS: Record<OutsourcingHandoffStatus, string> = {
  Preparing: 'Stopping internal jobs, opening transfer-list slots',
  Ready: 'Slots free, about to submit to provider',
  Submitting: 'Provider order create in flight',
  SubmissionUnknown: 'Create call timed out ambiguously — needs manual reconciliation',
  Active: 'Provider is transferring coins',
  Finalizing: 'Provider done, local cleanup pass running',
  NeedsCredentials: 'Wrong password or exhausted backup codes — needs correction',
  NeedsReview: 'Needs manual review before it can continue',
  Completed: 'Terminal — completed successfully',
  Failed: 'Terminal — failed',
  Canceled: 'Terminal — canceled',
};

const ACTION_REQUIRED_LABELS: Record<OutsourcingActionRequired, string> = {
  contract_status_changed: 'Contract state changed elsewhere — re-check before acting',
  review_provider_error: 'Provider reported an error — see failure reason',
  review_provider_status: 'Unrecognized provider status — needs a human look',
  partial_completion: 'Provider finished short of the target — review before closing',
  credentials_required: 'Password or backup codes needed',
  transfer_slots_unavailable: 'Could not free enough transfer-list slots',
  account_session_unavailable: 'Could not close/reach the local browser session',
};

@Component({
  selector: 'app-outsourcing-handoff-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './outsourcing-handoff-dialog.html',
  styleUrl: './outsourcing-handoff-dialog.scss',
})
export class OutsourcingHandoffDialog implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _dialog = inject(MatDialog);

  loading = true;
  loadError = false;
  notFound = false;
  handoff: OutsourcingHandoffResponse | null = null;

  starting = false;
  startError: string | null = null;

  actionLoading = false;
  actionError: string | null = null;

  credsPassword = '';
  credsBackupCodes = '';

  startScreenshotUrl: string | null = null;
  endScreenshotUrl: string | null = null;
  startScreenshotLoading = false;
  endScreenshotLoading = false;
  startScreenshotError: string | null = null;
  endScreenshotError: string | null = null;

  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private _destroyed = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OutsourcingHandoffDialogData,
    private dialogRef: MatDialogRef<OutsourcingHandoffDialog>,
    private service: PlayerAuctionService,
  ) { }

  ngOnInit(): void {
    this._destroyRef.onDestroy(() => {
      this._destroyed = true;
      this._clearTimer();
      this._revokeUrls();
    });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.service.getOutsourcingHandoff(this.data.contract.id).subscribe({
      next: (handoff) => {
        this.handoff = handoff;
        this.notFound = false;
        this.loading = false;
        this._scheduleNextPoll();
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 404) {
          this.notFound = true;
          this.handoff = null;
        } else {
          this.loadError = true;
        }
      },
    });
  }

  get isTerminal(): boolean {
    return !!this.handoff && (OUTSOURCING_HANDOFF_TERMINAL_STATUSES as readonly string[]).includes(this.handoff.status);
  }

  get statusDescription(): string {
    return this.handoff ? HANDOFF_STATUS_LABELS[this.handoff.status] : '';
  }

  get actionRequiredDescription(): string | null {
    return this.handoff?.action_required ? ACTION_REQUIRED_LABELS[this.handoff.action_required] : null;
  }

  getStatusClass(status: OutsourcingHandoffStatus): string {
    switch (status) {
      case 'Completed': return 'handoff-status-completed';
      case 'Failed': return 'handoff-status-failed';
      case 'Canceled': return 'handoff-status-canceled';
      case 'NeedsCredentials':
      case 'NeedsReview':
      case 'SubmissionUnknown':
        return 'handoff-status-needs-attention';
      default:
        return 'handoff-status-active';
    }
  }

  startHandoff(): void {
    if (this.starting) return;
    this.starting = true;
    this.startError = null;
    this.service.startOutsourcingHandoff(this.data.contract.id).subscribe({
      next: (handoff) => {
        this.starting = false;
        this.handoff = handoff;
        this.notFound = false;
        this._scheduleNextPoll();
      },
      error: (err) => {
        this.starting = false;
        this.startError = err?.error?.error || 'Failed to start handoff';
      },
    });
  }

  retry(): void {
    if (this.actionLoading || !this.handoff) return;
    this.actionLoading = true;
    this.actionError = null;
    this.service.retryOutsourcingHandoff(this.data.contract.id).subscribe({
      next: (handoff) => {
        this.actionLoading = false;
        this.handoff = handoff;
        this._scheduleNextPoll();
      },
      error: (err) => {
        this.actionLoading = false;
        this.actionError = err?.error?.error || 'Failed to retry handoff';
      },
    });
  }

  onBackupCodesPaste(event: ClipboardEvent): void {
    const codes = pastedBackupCodes(event);
    this.credsBackupCodes = [...parseBackupCodes(this.credsBackupCodes), ...codes].join(', ');
  }

  submitCredentials(): void {
    if (this.actionLoading) return;
    const request: { password?: string; backup_codes?: string[] } = {};
    if (this.credsPassword.trim()) request.password = this.credsPassword.trim();
    if (this.credsBackupCodes.trim()) {
      request.backup_codes = parseBackupCodes(this.credsBackupCodes).slice(0, 6);
    }
    if (!Object.keys(request).length) {
      this.actionError = 'Enter a password or at least one backup code.';
      return;
    }
    this.actionLoading = true;
    this.actionError = null;
    this.service.correctOutsourcingHandoffCredentials(this.data.contract.id, request).subscribe({
      next: (handoff) => {
        this.actionLoading = false;
        this.handoff = handoff;
        this.credsPassword = '';
        this.credsBackupCodes = '';
        this._scheduleNextPoll();
      },
      error: (err) => {
        this.actionLoading = false;
        this.actionError = err?.error?.error || 'Failed to update credentials';
      },
    });
  }

  loadScreenshot(type: 'start' | 'end'): void {
    if (type === 'start') {
      this.startScreenshotLoading = true;
      this.startScreenshotError = null;
    } else {
      this.endScreenshotLoading = true;
      this.endScreenshotError = null;
    }
    this.service.getOutsourcingHandoffScreenshot(this.data.contract.id, type).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        if (type === 'start') {
          this.startScreenshotLoading = false;
          if (this.startScreenshotUrl) URL.revokeObjectURL(this.startScreenshotUrl);
          this.startScreenshotUrl = url;
        } else {
          this.endScreenshotLoading = false;
          if (this.endScreenshotUrl) URL.revokeObjectURL(this.endScreenshotUrl);
          this.endScreenshotUrl = url;
        }
      },
      error: (err) => {
        const msg = this._screenshotErrorMessage(err?.status);
        if (type === 'start') {
          this.startScreenshotLoading = false;
          this.startScreenshotError = msg;
        } else {
          this.endScreenshotLoading = false;
          this.endScreenshotError = msg;
        }
      },
    });
  }

  private _screenshotErrorMessage(status?: number): string {
    switch (status) {
      case 404: return 'Handoff not found.';
      case 409: return 'No provider order yet — nothing to screenshot.';
      case 422: return 'Provider does not support screenshots.';
      case 502: return 'Provider unreachable or errored.';
      default: return 'Failed to load screenshot.';
    }
  }

  private _scheduleNextPoll(): void {
    this._clearTimer();
    if (!this.handoff || this._destroyed || this.isTerminal) return;
    this._pollTimer = setTimeout(() => this.load(), POLL_INTERVAL_MS);
  }

  private _clearTimer(): void {
    if (this._pollTimer !== null) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private _revokeUrls(): void {
    if (this.startScreenshotUrl) URL.revokeObjectURL(this.startScreenshotUrl);
    if (this.endScreenshotUrl) URL.revokeObjectURL(this.endScreenshotUrl);
  }

  formatCoins(n: number): string {
    return (n ?? 0).toLocaleString();
  }

  formatDate(d: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleString();
  }

  /** Public tracking page for the provider order. Deliberately provider-neutral: neither the path nor the query names the provider. */
  copyBuyerLink(): void {
    const h = this.handoff;
    if (!h?.provider_order_id) return;
    const url = `${window.location.origin}/pa/${encodeURIComponent(h.provider_order_id)}/tracking`;
    this._dialog.open(ShareLinkDialog, { data: { url }, width: '420px', maxWidth: '95vw' });
  }

  close(): void {
    this.dialogRef.close();
  }
}
